// Paleta institucional derivada de tres colores base (primario, secundario, acento) que viven
// en la base de datos. Produce las 21 claves que usan las páginas (antes hardcodeadas por
// institución en institutionConfig.js); `tema` permite sobrescribir cualquiera de ellas.
// Sin dependencias: mezclas lineales en RGB, suficientes para gradientes y sombras.

export const CLAVES_PALETA = [
  'primary', 'primaryDark', 'primaryLight',
  'headerGradientStart', 'headerGradientMid', 'headerGradientEnd',
  'headerBorder', 'headerShadowPrimary', 'headerShadowSecondary',
  'headerDarkGradientStart', 'headerDarkGradientMid', 'headerDarkGradientEnd', 'headerDarkBorder',
  'accentFrom', 'accentTo',
  'cardGradientFrom', 'cardGradientVia', 'cardGradientTo',
  'badgeText', 'ctaText', 'ctaTextHover',
];

// Colores neutros de la plataforma (PUSAH) cuando no hay institución resuelta.
export const COLORES_NEUTROS = {
  primario: '#334155',
  secundario: '#0f172a',
  acento: '#2563eb',
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const esHex = (valor) => typeof valor === 'string' && HEX.test(valor.trim());

export const hexARgb = (hex) => {
  let h = hex.trim().slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const canal = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

export const rgbAHex = ({ r, g, b }) => `#${canal(r)}${canal(g)}${canal(b)}`;

// Mezcla lineal entre dos colores: t=0 devuelve `a`, t=1 devuelve `b`.
export const mezclar = (a, b, t) => {
  const A = hexARgb(a);
  const B = hexARgb(b);
  return rgbAHex({
    r: A.r + (B.r - A.r) * t,
    g: A.g + (B.g - A.g) * t,
    b: A.b + (B.b - A.b) * t,
  });
};

export const oscurecer = (hex, factor) => mezclar(hex, '#000000', factor);
export const aclarar = (hex, factor) => mezclar(hex, '#ffffff', factor);

export const rgba = (hex, alpha) => {
  const { r, g, b } = hexARgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * @param {{ primario?: string, secundario?: string, acento?: string, tema?: object }} colores
 * @returns {Record<string, string>} paleta con las 21 claves
 */
export const derivarPaleta = (colores = {}) => {
  const base = colores || {};
  const primario = esHex(base.primario) ? base.primario.trim().toLowerCase() : COLORES_NEUTROS.primario;
  const secundario = esHex(base.secundario) ? base.secundario.trim().toLowerCase() : oscurecer(primario, 0.25);
  const acento = esHex(base.acento) ? base.acento.trim().toLowerCase() : primario;
  const medio = mezclar(primario, secundario, 0.5);

  const paleta = {
    primary: primario,
    primaryDark: oscurecer(primario, 0.3),
    primaryLight: aclarar(primario, 0.15),
    headerGradientStart: primario,
    headerGradientMid: medio,
    headerGradientEnd: secundario,
    headerBorder: rgba(primario, 0.3),
    headerShadowPrimary: rgba(primario, 0.25),
    headerShadowSecondary: rgba(primario, 0.15),
    headerDarkGradientStart: oscurecer(primario, 0.3),
    headerDarkGradientMid: oscurecer(medio, 0.3),
    headerDarkGradientEnd: oscurecer(secundario, 0.3),
    headerDarkBorder: rgba(oscurecer(primario, 0.3), 0.5),
    accentFrom: primario,
    accentTo: secundario,
    cardGradientFrom: aclarar(primario, 0.1),
    cardGradientVia: medio,
    cardGradientTo: secundario,
    badgeText: secundario,
    ctaText: acento,
    ctaTextHover: oscurecer(acento, 0.15),
  };

  // Overrides del tema: sólo claves conocidas con valor de texto no vacío.
  const tema = base.tema && typeof base.tema === 'object' ? base.tema : {};
  CLAVES_PALETA.forEach((clave) => {
    const valor = tema[clave];
    if (typeof valor === 'string' && valor.trim()) paleta[clave] = valor.trim();
  });

  return paleta;
};

export const PALETA_NEUTRA = derivarPaleta(COLORES_NEUTROS);
